<script lang="ts">
	import { Button, Input, InputV2 } from '@Shelly/shelly-design-system';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	import { format } from 'svelte-i18n';
	import SettingGuide from '../SettingGuide.svelte';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { validateIpWithPort, validatePort } from '../../../helpers/validators';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;
	export let isOffline;

	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	let destinationUrl: string = settings?.sys?.rpc_udp?.dst_addr;
	let listenPort: string = settings?.sys?.rpc_udp?.listen_port;
	let loading: boolean;
	let destinationError: string;
	let listenError: string;

	$: disabled = destinationError || listenError;
	$: InputC = $showNewLayout ? InputV2 : Input;

	const setRpc = async () => {
		validate();
		if (!destinationError && !listenError) {
			loading = true;
			const params = {
				config: {
					rpc_udp: {
						dst_addr:
							(typeof destinationUrl === 'string' && destinationUrl.length < 1) ||
							destinationUrl === null
								? null
								: destinationUrl,
						listen_port:
							(typeof listenPort === 'string' && listenPort.length < 1) ||
							listenPort === null
								? null
								: Number(listenPort),
					},
				},
			};
			const res = await gen2Request(device, 'Sys.SetConfig', params);
			onSettingsResponse(res, device);
			loading = false;
		}
	};
	const validate = () => {
		destinationError =
			!validateIpWithPort(destinationUrl) && destinationUrl !== null && destinationUrl.length > 0
				? $format('invalid_ip_with_port_desc', {
						default: 'Invalid destination address',
					})
				: undefined;
		listenError =
			!validatePort(listenPort) &&
			listenPort !== null &&
			destinationUrl !== null &&
			destinationUrl.length > 0
				? $format('invalid_port_desc', {
						default: 'Invalid port',
					})
				: undefined;
	};
	const validateAndSend = () => {
		validate();
		setRpc();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: setRpc,
				loading,
				disabled: disabled || isOffline,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<SettingGuide
	text={$format('settings_guide_rpc_over_udp', {
		default:
			'Send and receive device commands over UDP on your local network. Set the destination host and port, and optionally a listen port.',
	})}
/>

<svelte:component
	this={InputC}
	aria-labelledby="input-55l7m30"
	class="mb-s"
	size="m"
	label={$format('destination_description', {
		default: 'Destination address and port',
	})}
	info={$format('example_placeholder', {
		default: 'Example: 192.168.1.1:1010',
		values: { example: '192.168.1.1:1010' },
	})}
	bind:value={destinationUrl}
	error={destinationError}
	type="text"
	icon={null}
	on:click={() => {
		destinationError = undefined;
	}}
	on:focus={() => {
		destinationError = undefined;
	}}
	on:input={() => {
		if (destinationError) {
			destinationError = undefined;
		}
	}}
	on:enter={validateAndSend}
	on:change={validate}
	disabled={isOffline}
	id="input-55l7m30"
/>
<svelte:component
	this={InputC}
	aria-labelledby="input-jwbqngk"
	class="mb-s"
	size="m"
	label={$format('listen_port_description', {
		default: 'Listening port',
	})}
	info={$format('example_placeholder', {
		default: 'Example: 1010',
		values: { example: '1010' },
	})}
	bind:value={listenPort}
	error={listenError}
	type="number"
	icon={null}
	on:click={() => {
		listenError = undefined;
	}}
	on:focus={() => {
		listenError = undefined;
	}}
	on:input={() => {
		if (listenError) {
			listenError = undefined;
		}
	}}
	on:enter={validateAndSend}
	on:change={validate}
	disabled={isOffline}
	id="input-jwbqngk"
/>
{#if !$showNewLayout}
	<Button {loading} disabled={disabled || isOffline} size="m" width="full" on:click={setRpc}>
		{$format('button_save')}
	</Button>
{/if}
